import Head from 'next/head'
import { useEffect, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import Linked from 'shared-components/service/Linked'
import AppImage from 'shared-components/ui/AppImage'
import styles from './ProductHero.module.css'
import FeaturedScores from './FeaturedScores'
import OwnerRailAsk from 'components/owner-notes/OwnerRailAsk'
import OwnerNotesReader from 'components/owner-notes/OwnerNotesReader'
import { badgeToImage } from './badgeImage'
import type {
  DealHuntProductLine,
  RatingDistributionsResponse,
  SpecDistributionsResponse,
} from 'lib/dealhunt/types'
import type { SoundGuysReviewModel } from 'lib/dealhunt/review'
import {
  amazonUrl,
  dealHuntProductHref,
  formatCents,
  productDisplayName,
  relativeTime,
} from 'lib/dealhunt/url'
import { publicConfig } from 'config'
import { videoHref, videoTypeLabel } from 'lib/dealhunt/video'
import { useCatalogEvent } from 'lib/analytics/catalog'
import { decodeEntities } from 'lib/decode-entities'

interface ProductHeroProps {
  data: DealHuntProductLine
  /** Only `class` is used here (FeaturedScores peer-set fallback); the per-spec
   *  distributions themselves now render in the Key Specs section. */
  specDistributions: SpecDistributionsResponse | null
  /** SoundGuys review model (featured-score gauges); null hides the scores card. */
  review: SoundGuysReviewModel | null
  /** Class score distributions; null degrades the scores card to rings only. */
  ratingDistributions: RatingDistributionsResponse | null
  /** Lab-section anchor + tab summary for the scores-card bridge; null when no lab curves. */
  labHref?: string | null
  labSummary?: string | null
}

type GalleryItem =
  | { kind: 'image'; thumb: string; src: string; alt: string }
  | { kind: 'video'; thumb: string; href: string; alt: string; type: string | null }

const ProductHero: React.VFC<ProductHeroProps> = ({
  data,
  specDistributions,
  review,
  ratingDistributions,
  labHref = null,
  labSummary = null,
}) => {
  const { node, primaryProduct, aggregates, heroImage, brandNode } = data
  // This page's address: the owner asks' writer handoff (One Review Writer).
  const productHref = dealHuntProductHref(
    node.fullSlug,
    brandNode?.name ?? null,
    publicConfig('useTrailingSlash'),
    brandNode?.slug ?? null
  )
  // `enrichment.owners` — the rail's owner card data (design 3a); null = none yet.
  const owners = data.enrichment.owners ?? null

  // Peer-set fallback for the FeaturedScores header when rating-distributions is
  // unavailable (the per-spec distributions now live in the Key Specs section).
  const rankClass = specDistributions?.class ?? null

  const [shot, setShot] = useState(0)
  // Hover-to-play for the active video slide. Autoplay is only reliable while
  // muted (browser autoplay policy treats hover, unlike a click, as a non-gesture),
  // so hovering plays a muted preview; the viewer unmutes via the player controls.
  const [hoverPlay, setHoverPlay] = useState(false)
  const thumbsRef = useRef<HTMLDivElement>(null)

  const badge = data.sgBadge ?? null
  // Prefer DealHunt's canonical displayName (migration doc A2); fall back to the same
  // brandedTitle the category card uses until that field deploys.
  const title = productDisplayName(data)

  // Editorial tagline/dek. DealHunt now serves the SoundGuys review's `content.subtitle`
  // as top-level `tagline` (the real one-liner — writer-overridable in SG Studio); prefer
  // it. Fall back to `node.description`, which usually just echoes the brand+name (or is
  // null), so only show that when it actually differs from the title.
  const norm = (s: string): string => s.toLowerCase().replace(/\s+/g, ' ').trim()
  const desc = node.description?.trim() ?? ''
  const descSubtitle =
    desc &&
    norm(desc) !== norm(node.name) &&
    norm(desc) !== norm(`${brandNode?.name ?? ''} ${node.name}`)
      ? desc
      : null
  const subtitle = data.tagline?.trim() || descSubtitle

  const domain = primaryProduct?.domain ?? 'US'
  const priceCents = primaryProduct?.currentPriceCents ?? aggregates.bestPriceCents
  const price = formatCents(priceCents, domain)
  const ctaHref = primaryProduct
    ? amazonUrl(primaryProduct.asin, primaryProduct.domain, primaryProduct.shortIoUrl)
    : null

  // No live price → fall back to DealHunt's MSRP (when provided) instead of an empty
  // "Price unavailable" state. priceFallback is non-null only when there's no live price.
  const fallback = priceCents === null ? data.priceFallback ?? null : null
  const fallbackPrice = fallback ? formatCents(fallback.priceCents, domain) : null

  // Gallery order: the Amazon product shot first, then the SoundGuys review-article
  // photos, then videos. When there's no review (no reviewImages), fall back to just
  // the product hero shot (no variant color shots) plus any videos.
  const reviewImages = [...data.enrichment.reviewImages].sort((a, b) => a.order - b.order)
  const videoItems: GalleryItem[] = data.enrichment.videos
    .map((v): GalleryItem | null => {
      const href = videoHref(v)
      if (!href || !v.thumbnailUrl) {
        return null
      }
      return {
        kind: 'video',
        thumb: v.thumbnailUrl,
        href,
        alt: v.title ?? node.name,
        type: videoTypeLabel(v.videoType),
      }
    })
    .filter((v): v is GalleryItem => v !== null)

  let items: GalleryItem[]
  if (reviewImages.length) {
    // Brand + model, matching the H1 — `node.name` alone is the bare leaf
    // ("QuietComfort 35 II"), which throws away the strongest Google Images signal.
    const productShot: GalleryItem[] = heroImage
      ? [{ kind: 'image', src: heroImage, thumb: heroImage, alt: title }]
      : []
    const reviewItems: GalleryItem[] = reviewImages.map(img => ({
      kind: 'image',
      src: img.url,
      thumb: img.thumbnailUrl ?? img.url,
      alt: img.caption ? decodeEntities(img.caption) : title,
    }))
    items = [...productShot, ...reviewItems, ...videoItems]
  } else {
    const productShot: GalleryItem[] = heroImage
      ? [{ kind: 'image', src: heroImage, thumb: heroImage, alt: title }]
      : []
    items = [...productShot, ...videoItems]
  }

  const active = Math.min(shot, Math.max(0, items.length - 1))
  const activeItem = items[active] ?? null
  // The shot rendered on first paint — preload THIS one, not `activeItem`, so the
  // <link> is stable across gallery navigation instead of being added/removed per click.
  const firstItem = items[0] ?? null
  const preloadSrc = firstItem?.kind === 'image' ? firstItem.src : null
  const { track } = useCatalogEvent()
  const trackGallery = (action: 'prev' | 'next' | 'thumb', index: number): void => {
    const item = items[index]
    track('product_page_gallery_click', {
      action,
      position: index + 1,
      media_type: item?.kind ?? 'image',
      gallery_size: items.length,
    })
  }
  const go = (dir: number): void => {
    if (items.length) {
      trackGallery(dir < 0 ? 'prev' : 'next', (active + dir + items.length) % items.length)
    }
    setShot(s => (items.length ? (s + dir + items.length) % items.length : 0))
  }
  const trackBuy = (location: 'hero' | 'hero_msrp'): void =>
    track('product_page_buy_click', {
      location,
      price_cents: priceCents ?? null,
    })

  // Reset hover-play whenever the slide changes, so a freshly-shown video starts
  // from its thumbnail rather than inheriting the previous slide's hover state.
  useEffect(() => {
    setHoverPlay(false)
  }, [active])

  // Keep the selected thumbnail centered in the scroll strip.
  useEffect(() => {
    const strip = thumbsRef.current
    const el = strip?.children[active] as HTMLElement | undefined
    if (strip && el) {
      strip.scrollTo({
        left: el.offsetLeft - strip.clientWidth / 2 + el.clientWidth / 2,
        behavior: 'smooth',
      })
    }
  }, [active])

  const lastChecked = relativeTime(primaryProduct?.priceUpdatedAt)

  return (
    <section className={styles.hero}>
      {/* Left column: the gallery. `display: contents` on mobile lets the gallery and
          the info column's halves order independently as grid items (title → gallery →
          rail); at md it becomes the left flex column. */}
      {preloadSrc && (
        <Head>
          <link
            key="pdp-hero-preload"
            rel="preload"
            as="image"
            href={preloadSrc}
            // eslint-disable-next-line react/no-unknown-property
            fetchpriority="high"
          />
        </Head>
      )}
      <div className={styles.left}>
        <div className={styles.gallery}>
          <div className={styles.mainImage}>
            {!activeItem && <div className={styles.imagePlaceholder} aria-hidden="true" />}
            {activeItem?.kind === 'image' && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={activeItem.src}
                alt={activeItem.alt}
                // Intrinsic-size hint matching the CSS 1:1 box — reserves layout before
                // the image arrives (SEO audit MEDIUM: zero width/height across the PDP).
                width={600}
                height={600}
                className={styles.image}
                // This is the page's LCP element. `fetchpriority` + the <link rel="preload">
                // above start it in the first request round-trip instead of after the
                // CSS/JS. No `loading` attribute — eager is the default and the one thing
                // that must never be lazy here. Lowercase because React 18.2 predates the
                // camelCase prop and passes unknown lowercase attributes straight through
                // (same reason AppImage writes `imagesizes`).
                // eslint-disable-next-line react/no-unknown-property
                fetchpriority="high"
                decoding="async"
              />
            )}
            {activeItem?.kind === 'video' && (
              <div
                className={styles.playerWrap}
                onMouseEnter={() => setHoverPlay(true)}
                onMouseLeave={() => setHoverPlay(false)}
              >
                <ReactPlayer
                  key={activeItem.href}
                  className={styles.player}
                  src={activeItem.href}
                  // Hover swaps the static thumbnail (`light`) for the live player and
                  // autoplays it muted; leaving reverts to the thumbnail.
                  light={hoverPlay ? false : activeItem.thumb}
                  playing={hoverPlay}
                  muted={hoverPlay}
                  controls
                  onPlay={() =>
                    track('product_page_gallery_video_play', {
                      destination_url: activeItem.href,
                      action: hoverPlay ? 'hover' : 'control',
                    })
                  }
                  width="100%"
                  height="100%"
                />
                {/* Type pill sits on the static thumbnail; hidden once playing so it
                  never overlaps YouTube's own title / CC / mute chrome. */}
                {activeItem.type && !hoverPlay && (
                  <span className={styles.videoType}>{activeItem.type}</span>
                )}
              </div>
            )}

            {/* SoundGuys editorial badge (Editor's Choice / Recommended) from data.sgBadge.
              Mirrors the category card: image shield preferred, key-colored text pill
              otherwise; renders nothing when the product has no badge. */}
            {badge?.imageUrl && (
              <AppImage
                layout="raw"
                sizes={64}
                className={styles.badgePicture}
                imageClassName={styles.badgeImg}
                image={badgeToImage(badge)}
              />
            )}
            {badge && !badge.imageUrl && (
              <span className={`${styles.badgePill} ${styles[`badge_${badge.key}`] ?? ''}`}>
                {badge.label}
              </span>
            )}

            {items.length > 1 && (
              <>
                <button
                  type="button"
                  className={`${styles.nav} ${styles.navPrev}`}
                  onClick={() => go(-1)}
                  aria-label="Previous image"
                >
                  {'‹'}
                </button>
                <button
                  type="button"
                  className={`${styles.nav} ${styles.navNext}`}
                  onClick={() => go(1)}
                  aria-label="Next image"
                >
                  {'›'}
                </button>
                <span className={styles.counter}>{`${active + 1} / ${items.length}`}</span>
              </>
            )}
          </div>

          {items.length > 1 && (
            <div className={styles.thumbs} ref={thumbsRef}>
              {items.map((item, i) => (
                <button
                  key={`${i}-${item.kind}`}
                  type="button"
                  className={styles.thumb}
                  data-active={i === active}
                  onClick={() => {
                    trackGallery('thumb', i)
                    setShot(i)
                  }}
                  aria-label={item.alt || `View item ${i + 1}`}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={item.thumb} alt="" className={styles.thumbImg} loading="lazy" />
                  {item.kind === 'video' && (
                    <span className={styles.thumbCaption}>
                      <span className={styles.thumbCaptionTri} aria-hidden="true" />
                      <span className={styles.thumbCaptionLabel}>{item.type ?? 'Watch'}</span>
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Owner voice fills the height the taller rail leaves under the gallery (design 4b). */}
        <OwnerNotesReader
          nodeId={node.id}
          productName={title}
          owners={owners}
          productHref={productHref}
        />
      </div>

      {/* The info column melts away on mobile (`display: contents`) so its two
          halves order independently as grid items: title ABOVE the gallery,
          rail (price + scores) below it. */}
      <div className={styles.info}>
        <div className={styles.titleBlock}>
          {brandNode && <span className={styles.brand}>{brandNode.name}</span>}
          <h1 className={styles.title}>{title}</h1>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        <div className={styles.rail}>
          {fallbackPrice ? (
            <div className={styles.priceBox}>
              <span className={styles.priceLabel}>{'MSRP'}</span>
              <div className={styles.priceRowFallback}>
                <span className={styles.price}>{fallbackPrice}</span>
                {ctaHref && (
                  <Linked
                    href={ctaHref}
                    className={styles.ctaInline}
                    target="_blank"
                    rel="nofollow sponsored noopener"
                    onClick={() => trackBuy('hero_msrp')}
                  >
                    {'View on Amazon'}
                    <span className={styles.ctaArrow} aria-hidden="true">
                      {'→'}
                    </span>
                  </Linked>
                )}
              </div>
              <p className={styles.fallbackNote}>
                {
                  "Manufacturer's suggested retail price at launch. Check retailers for current availability."
                }
              </p>
            </div>
          ) : (
            <div className={styles.priceBox}>
              <span className={styles.priceLabel}>{`Current price · Amazon ${domain}`}</span>
              <div className={styles.priceRow}>
                <span className={styles.price}>{price ?? 'Price unavailable'}</span>
              </div>
              {ctaHref && (
                <Linked
                  href={ctaHref}
                  className={styles.cta}
                  target="_blank"
                  rel="nofollow sponsored noopener"
                  onClick={() => trackBuy('hero')}
                >
                  {'Check Price on Amazon'}
                  <span className={styles.ctaArrow} aria-hidden="true">
                    {'→'}
                  </span>
                </Linked>
              )}
              <p className={styles.checked}>
                {lastChecked ? `Price last checked ${lastChecked}` : 'Price tracked by DealHunt'}
              </p>
            </div>
          )}

          {review && (
            <FeaturedScores
              review={review}
              ratingDistributions={ratingDistributions}
              rankClass={rankClass}
              labHref={labHref}
              labSummary={labSummary}
            />
          )}
          <OwnerRailAsk
            nodeId={node.id}
            productName={title}
            owners={owners}
            productHref={productHref}
          />
        </div>
      </div>
    </section>
  )
}

export default ProductHero
